import React, { useMemo, useRef, useState, useCallback } from 'react';

const defaultPrizes = ['৳7','৳14','৳21','৳178','৳28','৳35','৳999','৳9999'];
const defaultRestricted = new Set(['৳178','৳999','৳9999']);

export default function FreeSpin({
  apiBaseUrl,
  token = (typeof window !== 'undefined' ? localStorage.getItem('token') : null),
  logoUrl,
  prizes = defaultPrizes,
  restricted = defaultRestricted,
  onResult,
  userData,
  transactions,
}) {
  const wheelRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [pendingIndex, setPendingIndex] = useState(null);
  const [wonAmount, setWonAmount] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [errorAlreadyClaimed, setErrorAlreadyClaimed] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const sliceCount = prizes.length;
  const sliceAngle = 360 / sliceCount;

  const allowedIndexes = useMemo(() => (
    prizes
      .map((p, i) => (restricted.has(p) ? -1 : i))
      .filter((i) => i !== -1)
  ), [prizes, restricted]);

  const normalize = (angle) => {
    let a = angle % 360;
    if (a < 0) a += 360;
    return a;
  };

  const centerAngleOf = (index) => index * sliceAngle + sliceAngle / 2;

  const spinToIndex = useCallback((targetIndex) => {
    const current = normalize(rotation);
    const center = centerAngleOf(targetIndex);
    const margin = Math.max(2, Math.min(8, sliceAngle / 6));
    const epsRange = (sliceAngle / 2) - margin;
    const epsilon = (Math.random() * 2 * epsRange) - epsRange;
    const desired = normalize(360 - (center + epsilon));
    let baseDelta = desired - current;
    if (baseDelta < 0) baseDelta += 360;
    const extraTurns = 5 + Math.floor(Math.random() * 3); // 5..7 turns
    const totalDelta = extraTurns * 360 + baseDelta;

    setSpinning(true);
    setPendingIndex(targetIndex);
    setShowResult(false);
    setRotation((prev) => prev + totalDelta);
  }, [rotation, sliceAngle]);

  const hasDepositLast3Days = useMemo(() => {
    if (!Array.isArray(transactions)) return false;
    const now = new Date();
    const threeDaysAgo = new Date(now);
    threeDaysAgo.setDate(now.getDate() - 3);
    return transactions.some(tx => {
      try {
        const when = new Date(tx.created_at);
        return tx.action === 'deposit' && Number(tx.key) === 1 && when >= threeDaysAgo && when <= now;
      } catch (_) { return false; }
    });
  }, [transactions]);

  const hasClaimedToday = useMemo(() => {
    if (!Array.isArray(transactions)) return false;
    const today = new Date();
    return transactions.some(tx => {
      try {
        const when = new Date(tx.created_at);
        return tx.action === 'free_spin' && when.toDateString() === today.toDateString();
      } catch (_) { return false; }
    });
  }, [transactions]);

  const spin = useCallback(() => {
    // Enforcement & Checking System per Free-Spin-Rules.md
    if (spinning || allowedIndexes.length === 0) return;

    // 1. Login check
    const t = token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
    if (!t) {
      setModalMessage('You must be logged in to spin.');
      return;
    }

    // 2. Phone verification check (from server userData)
    const isVerified = (userData && userData.session_id === '1');
    if (!isVerified) {
      setModalMessage('Please verify your phone number first.');
      return;
    }

    // 3. Deposit check (from server transactions)
    if (!hasDepositLast3Days) {
      setModalMessage('You must deposit successfully in the last 3 days to spin.');
      return;
    }

    // 4. Daily limit check (from server transactions)
    if (hasClaimedToday) {
      setModalMessage('You have already claimed your free spin for today!');
      return;
    }

    // Passed all checks → spin
    const targetIndex = allowedIndexes[Math.floor(Math.random() * allowedIndexes.length)];
    spinToIndex(targetIndex);
  }, [spinning, allowedIndexes, token, userData, hasDepositLast3Days, hasClaimedToday, spinToIndex]);

  const onTransitionEnd = async (ev) => {
    if (ev.propertyName !== 'transform' || !spinning) return;
    setSpinning(false);

    let idx = pendingIndex;
    let finalAmount = 0;

    if (idx == null) {
      const finalAngle = normalize(rotation);
      const pointerAngle = normalize(360 - finalAngle);
      idx = Math.floor(pointerAngle / sliceAngle);
      const over = pointerAngle - (idx * sliceAngle);
      if (over > sliceAngle / 2 && idx < sliceCount - 1) idx++;
    }

    const amtLabel = prizes[idx];
    setWonAmount(amtLabel);
    setShowResult(true);
    finalAmount = parseInt(String(amtLabel).replace(/[^\d]/g, ''), 10);

    try {
      const res = await fetch(`${apiBaseUrl}/api/free/spin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ amount: finalAmount }),
      });

      if (res.status === 400) {
        // Server-side already claimed safeguard
        setErrorAlreadyClaimed(true);
        return;
      }
      if (!res.ok) {
        console.error('Free spin failed:', res.status);
        return;
      }
      if (onResult) onResult(finalAmount);
    } catch (err) {
      console.error('Free spin network error:', err);
    }
  };

  return (
    <div className="fs-overlay">
      <div className="fs-modal">
        <div className="fs-wheel-container">
          <div className="fs-pointer" />
          <div
            ref={wheelRef}
            className="fs-wheel"
            style={{ transform: `rotate(${rotation}deg)` }}
            onTransitionEnd={onTransitionEnd}
          >
            {prizes.map((p, i) => (
              <div
                className="fs-slice-number"
                key={i}
                style={{ '--angle': `${i * sliceAngle + sliceAngle / 2}deg` }}
              >
                {p}
              </div>
            ))}
            <div className="fs-center-circle">
              {logoUrl ? (
                <img src={logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button disabled={spinning} onClick={spin} className="fs-spin-btn">
            {spinning ? 'Spinning…' : 'SPIN'}
          </button>
        </div>

        {showResult && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              🎉 Congratulations!<br />
              You won <span className="fs-amount">{wonAmount}</span><br />
              <button className="fs-close-btn" onClick={() => setShowResult(false)}>OK</button>
            </div>
          </div>
        )}

        {errorAlreadyClaimed && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              ❌<br />
              Sorry, you already claimed free spin!<br />
              <button className="fs-close-btn" onClick={() => setErrorAlreadyClaimed(false)}>OK</button>
            </div>
          </div>
        )}

        {!!modalMessage && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              {modalMessage}<br />
              <button className="fs-close-btn" onClick={() => setModalMessage('')}>OK</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
